import { inject } from '@angular/core';
import { signalStore, withMethods } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { AutomaticMeasuresService } from '../services/api/generated/automatic-measures/automatic-measures.service';
import {
  AutomaticMeasureFollowUpValuesClearRangeResponse,
  AutomaticMeasureValuesBulkPayload,
  AutomaticMeasureValueUpdatePayload,
  GetAutomaticMeasureFollowUpValuesParams,
} from '../services/api/generated/model';
import { TableDataNumberFilter } from '../services/api/table.service';
import { RawAutomaticMeasureValue } from '../types/measure.interface';

export interface AutomaticValuesPage {
  rows: RawAutomaticMeasureValue[];
  totalCount: number;
  lastRow: number;
  /** True while the calc engine still owes this measure a recompute — feeds the
   *  poll-until-settled loop behind the 'Calcul en cours' badge. Optional in the
   *  swagger model, hence normalised with `?? false` at the consumer. */
  computing?: boolean;
}

export interface AutomaticValuesPageRequest {
  measureId: number;
  dateFrom: Date;
  dateTo: Date;
  startRow: number;
  endRow: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filter?: TableDataNumberFilter;
  includeExpected?: boolean;
}

export const AutomaticMeasureValueStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withMethods(() => {
    const api = inject(AutomaticMeasuresService);

    return {
      async loadPage({
        measureId,
        dateFrom,
        dateTo,
        startRow,
        endRow,
        sortField,
        sortOrder = 'desc',
        filter,
        includeExpected,
      }: AutomaticValuesPageRequest): Promise<AutomaticValuesPage> {
        const queryParams: GetAutomaticMeasureFollowUpValuesParams = {
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString(),
          startRow,
          endRow,
          sortOrder,
        };
        if (sortField) {
          queryParams.sortField = sortField;
        }
        if (includeExpected) {
          queryParams.includeExpected = true;
        }
        if (filter) {
          queryParams.filterField = filter.field;
          queryParams.filterType = filter.type;
          if (filter.value !== undefined && filter.value !== null) {
            queryParams.filterValue = String(filter.value);
          }
          if (filter.valueTo !== undefined && filter.valueTo !== null) {
            queryParams.filterValueTo = String(filter.valueTo);
          }
          if (filter.values && filter.values.length > 0) {
            queryParams.filterValues = filter.values.join(',');
          }
        }

        return firstValueFrom(api.getAutomaticMeasureFollowUpValues<AutomaticValuesPage>({ measureId }, queryParams));
      },

      async updateValue({
        measureId,
        valueId,
        inputValue,
      }: {
        measureId: number;
        valueId: number;
        inputValue: number | null;
      }): Promise<RawAutomaticMeasureValue> {
        // x-nullable swagger gap: the generated payload narrows inputValue to
        // number though the API accepts null to clear a reading.
        const payload = { inputValue } as AutomaticMeasureValueUpdatePayload;
        return firstValueFrom(api.putAutomaticMeasureFollowUpValueDetail<RawAutomaticMeasureValue>({ measureId, valueId }, payload));
      },

      async saveBatch({
        measureId,
        updates,
        creates,
      }: {
        measureId: number;
        updates: { id: number; inputValue: number | null }[];
        creates: { timestamp: Date; inputValue: number | null }[];
      }): Promise<void> {
        // Same x-nullable swagger gap as updateValue: the generated type
        // narrows inputValue to number though the API accepts null.
        const payload = {
          updates,
          creates: creates.map(c => ({ timestamp: c.timestamp.toISOString(), inputValue: c.inputValue })),
        } as AutomaticMeasureValuesBulkPayload;
        await firstValueFrom(api.postAutomaticMeasureFollowUpValuesBulk<void>({ measureId }, payload));
      },

      // Bulk-clear every acquired value in [dateFrom, dateTo] (inclusive) in one request, so a
      // scrolled cell-range / whole-column delete needn't enumerate ids the virtualised grid
      // never loaded. The server reports how many rows it deleted.
      async clearRange({
        measureId,
        dateFrom,
        dateTo,
      }: {
        measureId: number;
        dateFrom: Date;
        dateTo: Date;
      }): Promise<AutomaticMeasureFollowUpValuesClearRangeResponse> {
        return firstValueFrom(
          api.postAutomaticMeasureFollowUpValuesClearRange(
            { measureId },
            { dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString() },
          ),
        );
      },
    };
  }),
);
